import {noop} from 'lodash';
import React, {Component, createRef, FC, RefObject} from 'react';

import {KeyboardActionsEnum} from '../../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {RendererOf} from '../../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {InboxModel} from '../../../../../../../libs/shared/core/src/models/api/inboxModel';
import {
  BulkActionsTrackingTypesEnum,
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../../libs/shared/core/src/services/tracking/tracking';
import {CellsContext} from '../../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {KeyboardEventHandlers} from '../../../../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {PermissionCheck} from '../../../../../../../srcCommon/permissions/permissionCheck';
import {useMoveBulkActionPermission} from '../../../../../../../srcCommon/permissions/useBulkActionPermission';
import {findConversationNamespaces} from '../../../../../../helpers/models/conversationHelpers';
import {PermissionResult} from '../../../../../../helpers/permissionHelpers';
import {ConversationBaseModel} from '../../../../../../models/api/conversationModel';
import {PermissionsEnum} from '../../../../../../models/api/roleModel';
import {InboxesParameters} from '../../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {MoveDropdownContainer} from '../../topbar/move/moveDropdownContainer';

/*
 * Props.
 */

interface ConversationsMoveCoordinatorProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  conversations: ReadonlyArray<ConversationBaseModel>;
  conversationInboxes: ReadonlyArray<InboxModel>;
  inboxesParameters: InboxesParameters;
  isBulkActionRequired: boolean;
  onInterfaceEvent?: (action: BulkActionsTrackingTypesEnum, isBulk: boolean) => void;

  renderButton: RendererOf<ConversationsMoveButtonProps>;
}

export interface ConversationsMoveButtonProps {
  conversationInboxes: ReadonlyArray<InboxModel>;
  permissionResult: PermissionResult;
  onClick: () => void;
  hasHover: boolean;
  buttonRef?: RefObject<HTMLDivElement>;
}

/*
 * Component.
 */

interface ConversationsMoveCoordinatorState {
  isDropdownOpen: boolean;
  isDropdownOpenedByShortcut: boolean;
}

export class ConversationsMoveCoordinator extends Component<
  ConversationsMoveCoordinatorProps,
  ConversationsMoveCoordinatorState
> {
  constructor(props: ConversationsMoveCoordinatorProps) {
    super(props);

    this.state = {
      isDropdownOpen: false,
      isDropdownOpenedByShortcut: false,
    };
    this.buttonRef = createRef<HTMLDivElement>();
  }

  private readonly buttonRef: RefObject<HTMLDivElement>;

  /*
   * Event handlers.
   */

  private readonly toggleIsDropdownOpen = (isShortcut: boolean) => {
    // Tracking.
    if (!this.state.isDropdownOpen) {
      trackInterfaceEvent(InterfaceEventsEnum.MOVE_MENU, {step: 'open_menu'});
    }

    this.setState(({isDropdownOpen}) => ({
      isDropdownOpen: !isDropdownOpen,
      isDropdownOpenedByShortcut: isShortcut,
    }));
  };

  /*
   * Render.
   */

  render() {
    return (
      <BulkActionPermissionCheck
        isBulkActionRequired={this.props.isBulkActionRequired}
        inboxesParameters={this.props.inboxesParameters}
      >
        {(bulkActionPermission) => (
          <PermissionCheck
            namespaces={findConversationNamespaces(this.props.conversations)}
            permission={PermissionsEnum.CONVERSATION_MOVE}
            bulkActionPermission={bulkActionPermission}
          >
            {(permission) =>
              permission.isAllowed ? this.renderAllowed(permission) : this.renderNoAccessButton(permission)
            }
          </PermissionCheck>
        )}
      </BulkActionPermissionCheck>
    );
  }

  renderAllowed(permission: PermissionResult) {
    const keyHandlers: KeyboardEventHandlers = {
      [KeyboardActionsEnum.MOVE_TO_INBOX]: () => this.toggleIsDropdownOpen(true),
    };

    return (
      <KeyboardShortcuts handlers={keyHandlers}>
        {this.renderButton(permission)}
        {this.maybeRenderDropdown()}
      </KeyboardShortcuts>
    );
  }

  /*
   * Button.
   */

  private renderButton(permissionResult: PermissionResult) {
    return this.props.renderButton({
      onClick: () => this.toggleIsDropdownOpen(false),
      buttonRef: this.buttonRef,
      conversationInboxes: this.props.conversationInboxes,
      permissionResult,
      hasHover: this.state.isDropdownOpen,
    });
  }

  private renderNoAccessButton(permissionResult: PermissionResult) {
    return this.props.renderButton({
      onClick: noop,
      conversationInboxes: this.props.conversationInboxes,
      permissionResult,
      hasHover: false,
    });
  }

  /*
   * Dropdown.
   */

  private maybeRenderDropdown() {
    if (!this.state.isDropdownOpen) {
      return null;
    }

    return (
      <MoveDropdownContainer
        isOpenedByShortcut={this.state.isDropdownOpenedByShortcut}
        cellsContext={this.props.cellsContext}
        conversations={this.props.conversations}
        isBulkActionRequired={this.props.isBulkActionRequired}
        anchor={this.buttonRef}
        onRequestClose={() => this.toggleIsDropdownOpen(false)}
        panelId={this.props.panelId}
        inboxesParameters={this.props.inboxesParameters}
        onInterfaceEvent={this.props.onInterfaceEvent}
        shouldUntether
        shouldSkipApplyMaxSize
      />
    );
  }
}

/*
 * Helpers.
 */

interface BulkActionPermissionCheckProps {
  isBulkActionRequired: boolean;
  inboxesParameters: InboxesParameters;
  children: RendererOf<PermissionResult>;
}

const BulkActionPermissionCheck: FC<BulkActionPermissionCheckProps> = (props) => {
  const bulkActionPermission = useMoveBulkActionPermission(
    props.isBulkActionRequired,
    props.inboxesParameters,
  );

  return <>{props.children(bulkActionPermission)}</>;
};
